import React from 'react';
import { DatePicker as AntDatePicker } from 'antd';
import moment from 'moment';
import { DatePickerProps as AntDatePickerProps } from 'antd/lib/date-picker/interface';
import classNames from 'classnames';

import { i18nAntdLocale } from 'app/i18n';

import styles from './date-picker-component.module.scss';

interface OwnProps {
    value: string;
    onChange: (value: string | null) => void;
    onFocus?: () => void;
    onBlur?: () => void;
}

interface State {
    open?: boolean;
}

const DATE_VALUE_FORMAT: string = 'YYYY-MM-DD';

export type DatePickerProps = OwnProps & AntDatePickerProps;

class DatePickerComponent extends React.Component<DatePickerProps, State> {
    public readonly state: State = {
        open: false,
    };

    private readonly calendarContainer: React.RefObject<HTMLDivElement> = React.createRef<HTMLDivElement>();

    public render(): React.ReactNode {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars,no-unused-vars
        const { value, onChange: ignoreOnChange, onFocus, onBlur, format, className, ...rest } = this.props;
        const { open } = this.state;
        const { DatePicker } = i18nAntdLocale;
        const defaultFormat: string | undefined = DatePicker?.lang?.dateFormat;

        return (
            <div className={classNames(styles.datePicker, className)}>
                <AntDatePicker
                    {...rest}
                    format={format || defaultFormat}
                    showToday={false}
                    /*
                     * value property overrides selected month in internal calendar state,
                     * so it is used only when calendar is closed to reset it's state,
                     * otherwise defaultValue is used.
                     */
                    {...{ [open ? 'defaultValue' : 'value']: this.parseStringValue(value) }}
                    onOpenChange={this.handleOpenChange}
                    onChange={this.handleChange}
                    getCalendarContainer={this.getCalendarContainer}
                    /**
                     * To remove icon Fragment is used because default icon is shown if undefined
                     * or null is passed. Currently icon is shown with css because Antd only
                     * displayed suffixIcon when date picker was closed.
                     */
                    suffixIcon={<React.Fragment />}
                    allowClear={false}
                />
                <div ref={this.calendarContainer} />
            </div>
        );
    }

    private handleOpenChange = (open: boolean) => {
        const { onFocus, onBlur } = this.props;
        const handler = open ? onFocus : onBlur;

        this.setState({
            open,
        });

        if (handler) {
            handler();
        }
    };

    private parseStringValue(value: string): moment.Moment | undefined {
        if (!value) {
            return undefined;
        }
        return moment(value, DATE_VALUE_FORMAT);
    }

    private readonly handleChange: (value: moment.Moment | null) => void = (value: moment.Moment | null) => {
        const { onChange } = this.props;

        if (onChange) {
            onChange(value && value.format(DATE_VALUE_FORMAT));
        }
    };

    private readonly getCalendarContainer = (): HTMLDivElement => {
        if (!this.calendarContainer.current) {
            throw new Error('Date picker requested container which is not available yet.');
        }

        return this.calendarContainer.current;
    };
}

export { DatePickerComponent };
